import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTranslation } from "../i18n/I18nContext";
import { Button } from "../components/ui/button";
import { ArrowLeft, Play, Pause, RotateCcw, ChevronLeft, ChevronRight, Languages, CheckCircle, XCircle } from "lucide-react";

// Brand Logo — pratik oturumuyla BİREBİR aynı asset (görsel bütünlük).
const LOGO_URL = "https://customer-assets.emergentagent.com/job_b31fc984-f212-4811-9faa-4930fd1aea96/artifacts/xtug1gme_image.png";

const mdComponents = {
  h1: ({ node, ...props }) => <h1 className="text-2xl font-bold text-white mt-4 mb-3 font-['Nunito']" {...props} />,
  h2: ({ node, ...props }) => <h2 className="text-xl font-bold text-white mt-4 mb-2 font-['Nunito']" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-lg font-semibold text-red-400 mt-3 mb-2" {...props} />,
  p: ({ node, ...props }) => <p className="text-neutral-100 text-lg leading-relaxed my-3 break-words" {...props} />,
  strong: ({ node, ...props }) => <strong className="text-white font-bold" {...props} />,
  em: ({ node, ...props }) => <em className="text-neutral-200 italic" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-6 space-y-1.5 my-3 text-neutral-100 text-lg" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-6 space-y-1.5 my-3 text-neutral-100 text-lg" {...props} />,
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  hr: () => <hr className="border-neutral-800 my-4" />,
  table: ({ node, ...props }) => (
    <div className="my-4 overflow-x-auto rounded-lg border border-neutral-700">
      <table className="w-full text-left" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-neutral-800 text-white" {...props} />,
  th: ({ node, ...props }) => <th className="px-4 py-2 font-semibold border-b border-neutral-700" {...props} />,
  td: ({ node, ...props }) => <td className="px-4 py-2 text-neutral-100 border-b border-neutral-800/70" {...props} />,
  code: ({ node, ...props }) => <code className="px-1.5 py-0.5 rounded bg-neutral-800 text-red-300" {...props} />,
};

const mdComponentsSmall = {
  p: ({ node, ...props }) => <p className="text-sm text-white/70 italic leading-relaxed my-1.5 break-words" {...props} />,
  strong: ({ node, ...props }) => <strong className="text-white/80 font-semibold not-italic" {...props} />,
  em: ({ node, ...props }) => <em className="italic" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 space-y-1 my-1.5 text-sm text-white/70 italic" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 space-y-1 my-1.5 text-sm text-white/70 italic" {...props} />,
  li: ({ node, ...props }) => <li {...props} />,
  table: ({ node, ...props }) => (
    <div className="my-2 overflow-x-auto rounded border border-neutral-800">
      <table className="w-full text-left text-xs" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => <th className="px-3 py-1 font-medium text-white/70 border-b border-neutral-800" {...props} />,
  td: ({ node, ...props }) => <td className="px-3 py-1 text-white/60 border-b border-neutral-900" {...props} />,
};

export default function LessonTeach() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const audioRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [showTranslation, setShowTranslation] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({}); // { [questionIndex]: selectedOptionIndex }

  const { steps = [], grammarTopic } = location.state || {};
  const total = steps.length;
  const step = steps[index];

  useEffect(() => {
    if (!steps || steps.length === 0) {
      navigate("/dashboard", { replace: true });
    }
  }, [steps, navigate]);

  // On step change: autoplay this step's audio (translation toggle stays sticky for the whole session)
  useEffect(() => {
    const el = audioRef.current;
    if (el) {
      el.currentTime = 0;
      const p = el.play();
      if (p && p.catch) p.catch(() => {});
    }
  }, [index]);

  if (!step) return null;

  // "answers" kartının çevirisi sabit metin → kullanıcının ana dilinde i18n'den (LLM yok).
  const stepTranslation =
    step.translation || (step.type === "answers" ? t("lesson_answers_translation") : "");

  const quizScore =
    step.type === "answers" && step.questions
      ? step.questions.reduce(
          (acc, q, qi) => acc + (quizAnswers[qi] === q.correct_index ? 1 : 0),
          0
        )
      : 0;

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play();
    else el.pause();
  };

  const replay = () => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    el.play();
  };

  const goPrev = () => setIndex((i) => Math.max(0, i - 1));
  const goNext = () => {
    if (index >= total - 1) {
      navigate("/dashboard");
    } else {
      setIndex((i) => Math.min(total - 1, i + 1));
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex flex-col" data-testid="lesson-teach-page">
      {/* Header — pratik oturumuyla görsel bütünlük: aynı logo (ortada), aynı "Çıkış" üslubu (solda) */}
      <div className="bg-neutral-900 border-b border-neutral-800">
        <div className="relative flex items-center justify-between px-4 py-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard")}
            className="text-white/70 hover:text-white hover:bg-white/10"
            data-testid="lesson-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("exit_session")}
          </Button>
          {/* Logo — başlığın tam ortasında mutlak-konumlu (dil/etiket uzunluğundan bağımsız sabit kalır) */}
          <img
            src={LOGO_URL}
            alt="Talkivist"
            className="h-6 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          />
          <span className="text-sm text-neutral-400 shrink-0" data-testid="lesson-step-indicator">
            {index + 1} / {total}
          </span>
        </div>
        {/* progress bar */}
        <div className="h-1 bg-neutral-800">
          <div
            className="h-full bg-red-600 transition-all duration-300"
            style={{ width: `${((index + 1) / total) * 100}%` }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 pt-5 pb-40">
          {grammarTopic && index === 0 && (
            <h1 className="text-xl sm:text-2xl font-bold text-red-400 mb-1 font-['Nunito']" data-testid="lesson-topic-title">
              {grammarTopic}
            </h1>
          )}
          {step.title && (
            <h2 className="text-lg font-semibold text-white/90 mb-2" data-testid="lesson-step-title">
              {step.title}
            </h2>
          )}

          <div className="prose prose-invert max-w-none" data-testid="lesson-step-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {step.content}
            </ReactMarkdown>
          </div>

          {/* Translation (text only, never spoken) */}
          {stepTranslation && (
            <div className="mt-4 flex flex-col gap-2" data-testid="translation-section">
              <button
                type="button"
                onClick={() => setShowTranslation((v) => !v)}
                className="self-start inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-white bg-neutral-800/70 hover:bg-neutral-700 px-3 py-1.5 rounded-full transition-colors"
                data-testid="toggle-translation-btn"
              >
                <Languages className="w-4 h-4" />
                {showTranslation ? t("hide_translation") : t("show_translation")}
              </button>
              {showTranslation && (
                <div className="border-l-2 border-neutral-700 pl-3" data-testid="translation-text">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponentsSmall}>
                    {stepTranslation}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          )}

          {/* Quiz (interactive multiple choice) */}
          {step.type === "quiz" && step.questions && (
            <div className="mt-5 space-y-4" data-testid="quiz-questions">
              {step.questions.map((q, qi) => (
                <div
                  key={qi}
                  className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-4"
                  data-testid={`quiz-q-${qi}`}
                >
                  <p className="text-white font-semibold mb-3">{qi + 1}. {q.question}</p>
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => {
                      const selected = quizAnswers[qi] === oi;
                      return (
                        <button
                          key={oi}
                          type="button"
                          onClick={() => setQuizAnswers((prev) => ({ ...prev, [qi]: oi }))}
                          className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                            selected
                              ? "bg-red-500/15 border-red-500/60 text-white font-semibold"
                              : "bg-neutral-800/60 border-neutral-700 text-neutral-200 hover:bg-neutral-700/60"
                          }`}
                          data-testid={`quiz-q-${qi}-opt-${oi}`}
                        >
                          <span className="font-mono text-xs opacity-70">{String.fromCharCode(65 + oi)}.</span>
                          <span className="flex-1">{opt}</span>
                          {selected && <CheckCircle className="w-4 h-4 text-red-400 shrink-0" data-testid={`quiz-q-${qi}-selected`} />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Answer key (assessment of student's picks + score) */}
          {step.type === "answers" && step.questions && (
            <div className="mt-5 space-y-4" data-testid="quiz-answers">
              <div
                className="flex items-center justify-between bg-neutral-900/80 border border-neutral-700 rounded-xl px-4 py-3"
                data-testid="quiz-score"
              >
                <span className="text-neutral-300 text-sm font-medium">{t("lesson_score")}</span>
                <span className="text-white text-xl font-bold" data-testid="quiz-score-value">
                  {quizScore} / {step.questions.length}
                </span>
              </div>
              {step.questions.map((q, qi) => {
                const userPick = quizAnswers[qi];
                const answered = userPick !== undefined;
                const isRight = answered && userPick === q.correct_index;
                return (
                  <div
                    key={qi}
                    className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-4"
                    data-testid={`quiz-q-${qi}`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <p className="text-white font-semibold">{qi + 1}. {q.question}</p>
                      <span
                        className={`shrink-0 text-xs font-semibold px-2 py-1 rounded-full ${
                          isRight
                            ? "bg-emerald-500/15 text-emerald-300"
                            : "bg-red-500/15 text-red-300"
                        }`}
                        data-testid={`quiz-q-${qi}-status`}
                      >
                        {answered ? (isRight ? t("lesson_correct") : t("lesson_incorrect")) : t("lesson_not_answered")}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {q.options.map((opt, oi) => {
                        const isCorrect = oi === q.correct_index;
                        const isUserWrong = answered && oi === userPick && !isRight;
                        let cls = "bg-neutral-800/60 border-neutral-700 text-neutral-200";
                        if (isCorrect) cls = "bg-emerald-500/15 border-emerald-500/50 text-emerald-300 font-semibold";
                        else if (isUserWrong) cls = "bg-red-500/15 border-red-500/50 text-red-300 font-semibold line-through";
                        return (
                          <div
                            key={oi}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${cls}`}
                            data-testid={`quiz-q-${qi}-opt-${oi}`}
                          >
                            <span className="font-mono text-xs opacity-70">{String.fromCharCode(65 + oi)}.</span>
                            <span className="flex-1">{opt}</span>
                            {isCorrect && <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" data-testid={`quiz-q-${qi}-correct`} />}
                            {isUserWrong && <XCircle className="w-4 h-4 text-red-400 shrink-0" data-testid={`quiz-q-${qi}-wrong`} />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Bottom controls */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-neutral-800 bg-neutral-900/95 backdrop-blur">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={goPrev}
            disabled={index === 0}
            className="border-neutral-700 bg-neutral-800 text-white hover:bg-neutral-700 h-11 px-4 rounded-xl disabled:opacity-40"
            data-testid="lesson-prev-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t("lesson_prev")}
          </Button>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={togglePlay}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full h-11 w-11 p-0 flex items-center justify-center"
              data-testid="lesson-playpause-btn"
            >
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={replay}
              className="border-neutral-700 bg-neutral-800 text-white hover:bg-neutral-700 h-11 w-11 p-0 rounded-full flex items-center justify-center"
              data-testid="lesson-replay-btn"
              title={t("lesson_replay")}
            >
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>

          <Button
            type="button"
            onClick={goNext}
            className="bg-red-600 hover:bg-red-700 text-white h-11 px-4 rounded-xl"
            data-testid="lesson-next-btn"
          >
            {index >= total - 1 ? t("lesson_finish") : t("lesson_next")}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* audio for current step */}
      {step.audio && (
        <audio
          key={index}
          ref={audioRef}
          src={`data:audio/mp3;base64,${step.audio}`}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        />
      )}
    </div>
  );
}
